import { AbstractDate } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
} from "@radarly/models/dataviz";
import { SearchFragmentDemographic } from "@radarly/models/search";
import { isDemographicCardDataviz } from "../dataviz";
import { Card, DemographicCard } from "../model";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";

interface DemographicCardFactoryOptions {
  from?: AbstractDate;
  metric?: Metric;
  to?: AbstractDate;
  clause?: Clause;
}

export class DemographicCardFragmentsFactory extends CardFragmentsFactory {
  static create(input?: DemographicCardFactoryOptions) {
    const factory = new DemographicCardFragmentsFactory();
    return factory.getFragments(input);
  }
  static createFromCard(card: Card, input?: DemographicCardFactoryOptions) {
    const factory = new DemographicCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    return factory.getFragments({
      clause: factory.getClauseFromCard(card),
      from,
      to,
      metric: factory.getMetricFromCardAndInput(card, input),
    });
  }
  private getFragments(
    input?: DemographicCardFactoryOptions
  ): [SearchFragmentDemographic] {
    return [this.getFragment(input)];
  }
  private getFragment(
    input?: DemographicCardFactoryOptions
  ): SearchFragmentDemographic {
    const metric = input?.metric || Metric.Doc;
    return {
      request: {
        query: this.getQuery(input),
        aggs: {
          metrics: { metrics: [metric] },
          genders: {
            metrics: [metric],
            aggs: {
              age: {
                metrics: [metric],
              },
            },
          },
        },
      },
    };
  }
}

export class DemographicCardFactory
  implements ICardFactory<DemographicCard, DemographicCardFactoryOptions>
{
  public static getInstance() {
    return new DemographicCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: DemographicCardFactoryOptions & {
      dataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<DemographicCard>().createCard(
      workspaceId,
      projectFocuses,
      "demographic",
      card.title,
      DemographicCardFragmentsFactory.createFromCard(card, input),
      this.createDataviz(input?.dataviz || card.dataviz)
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: DemographicCardFactoryOptions & {
      dataviz?: Dataviz["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<DemographicCard>().createCard(
      workspaceId,
      projectFocuses,
      "demographic",
      title,
      DemographicCardFragmentsFactory.create(input),
      this.createDataviz(
        input?.fullDataviz ??
          getDefaultDataviz(input?.dataviz || DatavizName.HorizontalBarChart)
      )
    );
  }
  public createDataviz(dataviz: Dataviz) {
    if (isDemographicCardDataviz(dataviz)) {
      return dataviz;
    } else {
      return getDefaultDataviz(
        DatavizName.Demographic
      ) as DemographicCard["dataviz"];
    }
  }
}
